import { z } from 'zod';
import {
  AuditSchema,
  InstantSchema,
  LotIdSchema,
  MoneySchema,
  PartyIdSchema,
  PlainDateSchema,
  ProductIdSchema,
  QuantitySchema,
  TenantScopedSchema,
  UserIdSchema,
} from './primitives.js';

/* ------------------------------------------------------------------ *
 * Stock, by lot.
 *
 * Perishable goods cannot be tracked as a single number per product.
 * A lot is a quantity that arrived together, with one expiry and one
 * origin — which is what a recall or a quality complaint is traced by.
 * The "on hand" figure everyone wants is a sum over lots, never a
 * stored counter that drifts.
 * ------------------------------------------------------------------ */

export const StockLotSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: LotIdSchema,
  productId: ProductIdSchema,
  productName: z.string().min(1),
  sku: z.string().min(1),
  /** Supplier's lot code from the crate label, when there is one. */
  lotCode: z.string().nullable().default(null),
  supplierId: PartyIdSchema.nullable().default(null),
  location: z.string().nullable().default(null),

  received: QuantitySchema,
  /** received minus everything moved out. */
  remaining: QuantitySchema,
  /** Reserved by picked-but-not-dispatched orders. */
  reserved: QuantitySchema.nullable().default(null),

  receivedAt: InstantSchema,
  bestBefore: PlainDateSchema.nullable().default(null),
  /** Weighted cost of this lot, for margin and for stock valuation. */
  unitCost: MoneySchema.nullable().default(null),
  status: z.enum(['available', 'quarantine', 'expired', 'written-off', 'depleted']).default('available'),
});
export type StockLot = z.infer<typeof StockLotSchema>;

export const StockMoveSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: z.string().min(1),
  lotId: LotIdSchema,
  productId: ProductIdSchema,
  kind: z.enum([
    'receipt',      // came in from a purchase
    'pick',         // went out to an order
    'return',       // came back from a customer
    'adjustment',   // a count corrected it
    'write-off',    // spoiled, broken, expired
    'transfer',     // moved location
  ]),
  quantity: QuantitySchema,
  reference: z.string().nullable().default(null),
  reason: z.string().nullable().default(null),
  byId: UserIdSchema.nullable().default(null),
  at: InstantSchema,
});
export type StockMove = z.infer<typeof StockMoveSchema>;

/** Aggregate view. Computed, never stored. */
export interface StockPosition {
  productId: string;
  productName: string;
  sku: string;
  onHand: import('./primitives.js').Quantity;
  reserved: import('./primitives.js').Quantity;
  available: import('./primitives.js').Quantity;
  /** Demand from open orders not yet covered by stock. */
  shortfall: import('./primitives.js').Quantity | null;
  lotCount: number;
  /** Earliest best-before across available lots. Drives the "use first" ordering. */
  nextExpiry: string | null;
  expiringSoonCount: number;
  valuation: import('./primitives.js').Money | null;
}
